import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from "react";
import { Briefcase, LogOut, MessagesSquare, Trash2 } from "lucide-react";
import {
  useSessionAttachments,
  type ChatAttachmentItem,
} from "@/app/routes/dashboard/api/useSessionAttachments";
import { useDeferredSend } from "@/app/routes/dashboard/api/useDeferredSend";
import { isImageFile } from "@/app/routes/dashboard/api/useBrandMedia";
import { WORKSHOP_ATTACH_ACCEPT, WORKSHOP_DOC_ACCEPT, WORKSHOP_PHOTO_ACCEPT } from "./workshopAccept";
import { findPendingInteraction } from "@/app/routes/dashboard/components/agentInteractions";
import { parseAskUserQuestions } from "@/app/routes/dashboard/components/askUserQuestion";
import { savedConceptIdeas } from "@/app/routes/dashboard/components/conceptIdeaCard";
import { generatedMediaRef } from "@/app/routes/dashboard/components/generatedMediaCard";
import { createdArtifactRef } from "@/app/routes/dashboard/components/createdArtifactCard";
import { canonicalizeChatModelId, useChatModels, WORKSHOP_AUTO_MODEL_ID } from "@/hooks/useChatModels";
import { useWorkshopModelStore } from "@/stores/workshopModelStore";
import { useAuthStore } from "@/stores/authStore";
import type {
  ChatModel,
  ChatToolEvent,
  ReasoningEffort,
  StreamingToolEvent,
} from "@/types/chat.type";
import type { WorkshopPerson, WorkshopQuickCreateItem, WorkshopQuickCreateRef, WorkshopSession } from "@/types/workshop.type";
import { useWorkshopQuickCreate } from "../api/useWorkshopQuickCreate";
import { addPick, pickId, refusedIds, removePick, unavailablePicks } from "../quickCreate";
import { QuickCreateGone } from "./QuickCreateParts";
import { getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import { unsentKey, unsentParts, useWorkshopChat } from "../api/useWorkshopChat";
import { useSharedWorkshopThread } from "../api/useSharedWorkshopThread";
import { joinQueued, useMessageQueue } from "../api/useMessageQueue";
import { THREAD_TRIM_AT, oldestStored } from "../api/threadPages";
import { useWorkshopSessions } from "../api/useWorkshopSessions";
import { workshopAttachmentEndpoints } from "../api/useWorkshopFiles";
import {
  newMediaPreviewId,
  useWorkshopMedia,
  useWorkshopMediaMutations,
} from "../api/useWorkshopMedia";
import type { AttachmentClaims } from "../../agent/rail/railFormat";
import { placeThreadFiles, toolSucceeded } from "../workshopThread";
import ConceptIdeaPreviewCard from "./ConceptIdeaPreviewCard";
import GeneratedMediaPreviewCard from "./GeneratedMediaPreviewCard";
import CreatedArtifactPreviewCard from "./CreatedArtifactPreviewCard";
import ShareThreadModal from "./ShareThreadModal";
import LeaveChatDialog from "./LeaveChatDialog";
import { useComposerVoice } from "../../agent/rail/useComposerVoice";
import { ThreadTypingLine } from "./SharedThreadStatus";
import WorkshopConversation from "./WorkshopConversation";
import type { SendFlight } from "../../agent/rail/sendFlight";
import { quotePassage } from "./docFormat";
import WorkshopComposer from "./WorkshopComposer";
import WorkshopThreadHead, { type RoomSection } from "./WorkshopThreadHead";
import type { ChatMenuAction } from "./WorkshopChatMenu";
import { useWorkshopProjects } from "../api/useWorkshopProjects";
import { useMoveSession } from "../api/useWorkshopMove";
import { sortProjects, useProjectSort } from "../workshopSort";
import { isArchived } from "./WorkshopParts";
import { useOptionalWorkshopDesk } from "./workshopDesk";
import { CompactNudge, QueuedMessages, UnsentMessage } from "./WorkshopThreadNotes";
import { ThreadDropTarget, useThreadDrop } from "./WorkshopAttach";
import WorkshopDialog from "./WorkshopDialog";
import { KitInput } from "@/app/routes/dashboard/components/kit/Input";
import { heldThreadKey } from "./WorkshopSidebar";
import { useWorkshopUndo } from "./WorkshopUndo";

/**
 * The workshop chat persists the picker per session (PUT /sessions/:id), and
 * that endpoint only accepts these efforts — so models expose exactly this
 * subset in the rail.
 */
const WORKSHOP_EFFORTS: ReasoningEffort[] = ["low", "medium", "high", "xhigh", "max"];
const WORKSHOP_EFFORT_SET = new Set<ReasoningEffort>(WORKSHOP_EFFORTS);

/** How close to the bottom still counts as being at the thread's newest end. */
const FOLLOW_SLACK_PX = 48;
/** This close to the top, the page before the oldest message is read in. */
const LOAD_OLDER_PX = 320;
/** Settled at the newest end this long, the pages read on the way up go again. */
const TRIM_SETTLE_MS = 600;
/** After a page above failed to load, it is not tried again for this long. */
const OLDER_RETRY_MS = 5_000;

/** Quality-first surface: default "high", not the platform's usual "low". */
function resolveWorkshopEffort(
  options: ReasoningEffort[] | null | undefined,
  preferred: ReasoningEffort | null,
): ReasoningEffort {
  if (preferred && options?.includes(preferred)) return preferred;
  if (!options?.length) return "high";
  if (options.includes("high")) return "high";
  return options[0];
}


/** Nudge Compact once the window is meaningfully full. */
const COMPACT_NUDGE_RATIO = 0.6;

/**
 * How long to wait before focusing a prefilled composer. The surface asking for
 * the prefill (the idea modal) closes as it asks, and until its exit animation
 * ends its focus scope still holds focus and would pull it straight back.
 */
const PREFILL_FOCUS_DELAY_MS = 160;

/** The API's cap on a thread's name. */
const THREAD_TITLE_MAX = 120;

/** A thread's title before its first message (the backend's default). */
const UNTITLED_THREAD = "New chat";
/** Matches WorkshopView: URL threadId meaning a blank thread, not a session row. */
const NEW_THREAD_REQUEST = "new";

/**
 * The backend names a thread with a small model, beside the first reply or
 * right after its first upload. The list refreshes when a reply ends, which can
 * beat the name on a short turn — so one more refresh follows, after this long.
 */
const THREAD_TITLE_REFRESH_MS = 6_000;

/**
 * First-send choreography: the hero sinks out over this long (ws-hero-sink),
 * and the enter window closes this long after the first transcript row lands
 * (ws-thread-rise is 80ms delay + 500ms run). Keep in sync with workshop.css.
 */
const HERO_EXIT_MS = 320;
const THREAD_ENTER_MS = 700;

export type WorkshopChatPanelHandle = {
  /** Put a drafted message in the composer for the user to review and send. Never sends. */
  prefillComposer: (message: string) => void;
  /**
   * A document's Rewrite (stage 3): the passage goes at the top of the
   * composer as a markdown quote, with no words of its own, and the caret waits
   * under it for the person to say what they want. Never sends.
   */
  quoteInComposer: (passage: string) => void;
};

type Props = {
  ref?: Ref<WorkshopChatPanelHandle>;
  brandId: string;
  projectId: string;
  projectName: string;
  /** The thread to show — the URL's pick (the left rail, a deep link). */
  requestedThreadId?: string | null;
  /** Tells the page which thread is open, so the URL and the rail follow. */
  onThreadChange?: (threadId: string | null) => void;
  archived?: boolean;
  /** Viewers can chat; write tools are withheld server-side. */
  canWrite: boolean;
  /** The agent created/updated an artifact — nudge the Artifacts section. */
  onArtifactWritten?: (artifactId: string | null) => void;
  /** The user asked to see a created artifact — open it in the side panel. */
  onOpenArtifact?: (artifactId: string) => void;
  /** The user asked to see a saved concept idea — open it in the side panel. */
  onOpenIdea?: (ideaId: string) => void;
  /** A turn saved or updated a concept idea — nudge the Ideas section. */
  onIdeaSaved?: (ideaId: string) => void;
  /** A turn pinned context — nudge the Context section. */
  onContextWritten?: (contextItemId: string | null) => void;
  /** A turn deployed an agent — nudge the Agents section. */
  onDeploymentWritten?: (deploymentId: string | null) => void;
  /** A turn generated an image into the brand library — nudge the Media section. */
  onMediaWritten?: (mediaId: string | null) => void;
  /** The agent attached a video from a link — nudge the Sources section. */
  onFileWritten?: (documentId: string) => void;
  /** The user attached a file or image here — show it in the project room. */
  onUploadAdded?: (tab: "media" | "context", itemId: string) => void;
  /** The user asked to see a generated image — open it in the side panel. */
  onOpenMedia?: (mediaId: string) => void;
  /** Open the contextual project room at a section, without leaving the conversation. */
  onOpenLibrary?: (section: RoomSection) => void;
  libraryOpen?: boolean;
  /** The section the pane shows (an open output counts as Outputs), lit in the head. */
  openRoomSection?: RoomSection | null;
  /** Phone: the model menu and attach become sheets. */
  isPhone?: boolean;
  /** Compact shell: the title capsule opens the sidebar sheet. */
  lane?: boolean;
  /** First message from the draft surface — auto-sent once the chat is ready. */
  seedMessage?: string | null;
  /** Model/effort from the draft surface; applied before the seed send. */
  seedModelId?: string | null;
  seedReasoningEffort?: ReasoningEffort | null;
  /** The skills and agents the first message starts (its pills); the words may be empty. */
  seedQuickCreates?: WorkshopQuickCreateRef[] | null;
  onSeedConsumed?: () => void;
  /** Files picked before this thread's composer existed. Uploaded once it is open. */
  seedFiles?: File[] | null;
  seedThreadId?: string | null;
  onSeedFilesConsumed?: () => void;
  composerFrom?: DOMRect | null;
  onComposerFromConsumed?: () => void;
  /** Opens the project's people (from the share dialog, when nobody is in it yet). */
  onManageProject?: () => void;
  /** Compact shell: the rail is a sheet; the thread's title opens it. */
  onOpenProjectSheet?: () => void;
  /** The open thread was deleted or left from here: the page moves off it. */
  onThreadGone?: (threadId: string) => void;
};

export default function WorkshopChatPanel({
  ref,
  brandId,
  projectId,
  projectName,
  requestedThreadId = null,
  onThreadChange,
  archived = false,
  canWrite,
  onArtifactWritten,
  onOpenArtifact,
  onOpenIdea,
  onIdeaSaved,
  onContextWritten,
  onDeploymentWritten,
  onMediaWritten,
  onFileWritten,
  onUploadAdded,
  onOpenMedia,
  onOpenLibrary,
  libraryOpen = false,
  openRoomSection,
  isPhone = false,
  lane = isPhone,
  seedMessage = null,
  seedModelId = null,
  seedReasoningEffort = null,
  seedQuickCreates = null,
  onSeedConsumed,
  seedFiles = null,
  seedThreadId = null,
  onSeedFilesConsumed,
  composerFrom = null,
  onComposerFromConsumed,
  onManageProject,
  onOpenProjectSheet,
  onThreadGone,
}: Props) {
  const [selectedSessionId, setSelectedSessionId] = useState<
    string | null | undefined
  >(undefined);
  const [text, setText] = useState("");
  const voice = useComposerVoice(brandId, setText);
  // The skills and agents the next message starts (its pills), and those the API refused.
  const [pills, setPills] = useState<WorkshopQuickCreateRef[]>([]);
  const [refused, setRefused] = useState<string[]>([]);
  const quickList = useWorkshopQuickCreate(brandId);
  const quickItems = quickList.data?.items;
  const unavailablePills = unavailablePicks(pills, quickItems, refused);
  const pickQuickCreate = (item: WorkshopQuickCreateItem) => {
    setPills((prev) => addPick(prev, item));
  };
  // The pills a send is carrying, read when the deferred send fires.
  const sendingPillsRef = useRef<WorkshopQuickCreateRef[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const undoToast = useWorkshopUndo();
  const [shareOpen, setShareOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /** Where the words of the last send left the field (see useSendFlight). */
  const flightRef = useRef<SendFlight | null>(null);
  const seedSentRef = useRef<string | null>(null);
  // The same, for the render-time thread sync below (a ref cannot be read there).
  const [seedSent, setSeedSent] = useState<string | null>(null);
  const { data: rawModels = [] } = useChatModels({
    autoModelId: WORKSHOP_AUTO_MODEL_ID,
  });
  // Clamp every model's effort options to what the workshop endpoints accept.
  const models = useMemo(
    () =>
      rawModels.map((m) => ({
        ...m,
        reasoningOptions:
          m.reasoningOptions?.filter((o) => WORKSHOP_EFFORT_SET.has(o)) ?? null,
      })),
    [rawModels],
  );

  const {
    sessions,
    isLoading: sessionsLoading,
    createSession,
    updateSession,
    renameSession,
    deleteSession,
    removeParticipant,
    leaveOnUnload,
    compactSession,
    invalidateSessions,
  } = useWorkshopSessions(projectId);

  // A landing-composer send (seed) or `threadId=new` is a blank thread, never
  // the project's most recent one. The URL can lag one render behind the seed.
  // The landing's first send: words, pills, or both.
  const seedKey =
    seedMessage || seedQuickCreates?.length
      ? `${(seedQuickCreates ?? []).map(pickId).join(",")}|${seedMessage ?? ""}`
      : null;
  const wantsNewThread =
    requestedThreadId === NEW_THREAD_REQUEST || seedKey !== null;

  // Open the thread the URL asks for (else the most recent), then hold it: left
  // unpinned, a reply landing in another thread (another tab) would re-sort the
  // list and swap this one out.
  if (selectedSessionId === undefined && !sessionsLoading) {
    if (wantsNewThread) {
      setSelectedSessionId(null);
    } else {
      const requested =
        requestedThreadId && sessions.some((s) => s.id === requestedThreadId)
          ? requestedThreadId
          : null;
      setSelectedSessionId(requested ?? sessions[0]?.id ?? null);
    }
  }
  const currentSessionId =
    selectedSessionId === undefined
      ? wantsNewThread
        ? null
        : (sessions[0]?.id ?? null)
      : selectedSessionId;
  const currentSession =
    sessions.find((s) => s.id === currentSessionId) ?? null;

  // A shared thread has one assistant and several people: sends return at
  // once, and the reply, typing and presence arrive over the socket. A thread
  // is private (the SSE flow below) until its OP shares it.
  const viewer = useAuthStore((s) => s.user);
  const viewerPerson = useMemo(
    () => (viewer ? { id: viewer.id, name: viewer.name?.trim() || viewer.email } : null),
    [viewer],
  );
  const isShared = !!currentSession?.sharedAt;
  /** The viewer started this thread (or it is new): theirs to rename, share, delete. */
  const isThreadOwner = !currentSession || currentSession.isOwner !== false;
  const shared = useSharedWorkshopThread(isShared ? currentSessionId : null, viewerPerson);

  // Draft-surface pick — captured once so clearing the seed prop can't lose it.
  const bootstrapRef = useRef<{
    modelId: string;
    reasoningEffort: ReasoningEffort;
  } | null>(
    seedModelId
      ? {
          modelId: seedModelId,
          reasoningEffort: seedReasoningEffort ?? "high",
        }
      : null,
  );

  const desk = useOptionalWorkshopDesk();
  const { projects: allProjects } = useWorkshopProjects(brandId);
  const [projectSort] = useProjectSort();
  const moveProjects = sortProjects(allProjects.filter((p) => !isArchived(p)), projectSort);
  const move = useMoveSession(brandId);
  const moveChat = (toProjectId: string) => {
    const to = moveProjects.find((p) => p.id === toProjectId);
    if (!currentSession) return;
    move.mutate(
      { session: currentSession, toProjectId, toProjectName: to?.name ?? "the new project" },
      { onSuccess: () => desk?.openThread(toProjectId, currentSession.id) },
    );
  };

  /** The outputs a round of tool calls made, as cards: documents, pictures, ideas. */
  const renderOutputs = useCallback(
    (events: (ChatToolEvent | StreamingToolEvent)[]) => {
      const done = events.filter(toolSucceeded);
      const documents = done.flatMap((event) => createdArtifactRef(event) ?? []);
      const pictures = done.flatMap((event) => generatedMediaRef(event) ?? []);
      const ideas = onOpenIdea ? savedConceptIdeas(done) : [];
      if (documents.length + pictures.length + ideas.length === 0) return null;
      return (
        <div className="ws-outputs">
          {documents.map((doc) => (
            <CreatedArtifactPreviewCard
              key={doc.id}
              artifactId={doc.id}
              fallbackTitle={doc.title}
              onOpen={onOpenArtifact}
            />
          ))}
          {pictures.map((media) => (
            <GeneratedMediaPreviewCard
              key={media.id}
              projectId={projectId}
              mediaId={media.id}
              fallbackFileName={media.fileName}
              fallbackAspectRatio={media.aspectRatio}
              onOpen={onOpenMedia}
            />
          ))}
          {onOpenIdea
            ? ideas.map((idea) => (
                <ConceptIdeaPreviewCard
                  key={idea.id}
                  ideaId={idea.id}
                  fallbackTitle={idea.title}
                  onOpen={onOpenIdea}
                />
              ))
            : null}
        </div>
      );
    },
    // moveChat and the offer's state follow the chat; they change with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, onOpenArtifact, onOpenIdea, onOpenMedia, currentSession, moveProjects, move.isPending, isThreadOwner, canWrite, archived],
  );

  const handleIdeaSaved = useCallback(
    (ideaId: string | null) => {
      if (ideaId) onIdeaSaved?.(ideaId);
    },
    [onIdeaSaved],
  );

  const {
    messages,
    isLoadingMessages,
    messagesLoaded,
    streamingBlocks,
    isStreaming,
    selectedModel,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
    seedSelection,
    seededKey,
    markSeeded,
    contextTokens,
    streamError,
    unsentByThread,
    holdUnsent,
    takeUnsent,
    unsentRemainder,
    sendMessage,
    joinSend,
    stopStreaming,
    isStopping,
    clearMessages,
    paging: chatPaging,
  } = useWorkshopChat(brandId, projectId, isShared ? null : currentSessionId, {
    onSessionCreated: (s) => handleSessionCreated(s),
    onSessionsChanged: invalidateSessions,
    onArtifactWritten,
    onIdeaWritten: handleIdeaSaved,
    onContextWritten,
    onDeploymentWritten,
    onMediaWritten,
    onFileWritten,
  });

  // What the person sends while a private reply is written waits here, per
  // thread, and leaves as one message when the reply ends (see the flush below).
  const {
    list: listQueued,
    enqueue,
    remove: unqueue,
    restore: requeue,
    take: takeQueued,
    adopt: adoptQueued,
  } = useMessageQueue();

  const displayMessages = isShared ? shared.messages : messages;
  const displayLoadingMessages = isShared ? shared.isLoadingMessages : isLoadingMessages;
  const displayBlocks = isShared ? shared.streamingBlocks : streamingBlocks;
  const displayStreaming = isShared ? shared.isReplying : isStreaming;
  // The thread is read a page at a time (threadPages.ts).
  const paging = isShared ? shared.paging : chatPaging;

  // A thread picked outside the chat (the left rail, back/forward) after mount.
  // Adjusted during render, like the pin above. Refused while a reply streams:
  // the stream writes into whichever thread is open.
  const requestedKnown = sessions.some((s) => s.id === requestedThreadId);
  const [syncedThreadId, setSyncedThreadId] = useState(requestedThreadId);
  if (requestedThreadId !== syncedThreadId && !sessionsLoading) {
    setSyncedThreadId(requestedThreadId);
    if (!isStreaming) {
      if (requestedThreadId === NEW_THREAD_REQUEST) {
        // Don't tear down a thread the landing seed just created because the
        // URL caught up to `new` a render late.
        if (currentSessionId !== null && seedSent === null) {
          clearMessages();
          setSelectedSessionId(null);
        }
      } else if (requestedKnown && requestedThreadId !== currentSessionId) {
        clearMessages();
        setSelectedSessionId(requestedThreadId);
      }
    }
  }

  // Report the open thread so the URL and the rail follow. A refused or unknown
  // pick lands here too, which puts the URL back on the thread actually open.
  const threadResolved = selectedSessionId !== undefined && !sessionsLoading;
  useEffect(() => {
    if (!threadResolved) return;
    if (requestedThreadId === NEW_THREAD_REQUEST) {
      if (currentSessionId) onThreadChange?.(currentSessionId);
      return;
    }
    if (currentSessionId === requestedThreadId) return;
    if (isStreaming && requestedKnown) {
      queue.add({
        title: "Finish this reply first",
        description: "Wait for it, or stop it, before switching chats.",
        variant: "info",
      });
    }
    onThreadChange?.(currentSessionId);
  }, [
    threadResolved,
    currentSessionId,
    requestedThreadId,
    requestedKnown,
    isStreaming,
    onThreadChange,
  ]);

  // Called when a send (or an attach) opens the thread. Declared after the
  // hook it closes over; it only ever runs later, from an event.
  const handleSessionCreated = (s: WorkshopSession) => {
    // The landing seed's thread now exists: the URL catching up to `new` a
    // render late must not tear it down (the thread sync above).
    if (seedSentRef.current !== null) setSeedSent(seedSentRef.current);
    markSeeded(s.id);
    adoptQueued(s.id);
    setSelectedSessionId(s.id);
    // Persist the draft pick (or current rail pick) onto the new session row.
    const boot = bootstrapRef.current;
    const modelId = boot?.modelId ?? selectedModel?.id;
    const effort = boot?.reasoningEffort ?? selectedReasoningEffort;
    if (modelId && (modelId !== "auto" || effort !== "high")) {
      updateSession.mutate({
        sessionId: s.id,
        model: modelId,
        reasoningEffort: effort,
      });
    }
  };

  const titleRefreshTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (titleRefreshTimer.current !== null) window.clearTimeout(titleRefreshTimer.current);
    },
    [],
  );
  const refreshThreadTitleSoon = useCallback(() => {
    if (titleRefreshTimer.current !== null) window.clearTimeout(titleRefreshTimer.current);
    titleRefreshTimer.current = window.setTimeout(() => {
      titleRefreshTimer.current = null;
      invalidateSessions();
    }, THREAD_TITLE_REFRESH_MS);
  }, [invalidateSessions]);
  // A thread is (re)named by its first message — including one already named
  // after a file — so only a thread with messages is left alone.
  const threadAwaitsTitle =
    !currentSession || currentSession.title === UNTITLED_THREAD || displayMessages.length === 0;
  const watchForThreadTitle = useCallback(() => {
    if (threadAwaitsTitle) refreshThreadTitleSoon();
  }, [threadAwaitsTitle, refreshThreadTitleSoon]);

  const lastPickedModelId = useWorkshopModelStore((s) => s.modelId);
  const lastPickedEffort = useWorkshopModelStore((s) => s.reasoningEffort);
  const setLastPicked = useWorkshopModelStore((s) => s.setSelection);

  // Seed the picker from the session row exactly once per session switch, so a
  // later refetch never clobbers what the user just chose mid-session. Draft
  // bootstrap wins until a real session row exists; with no bootstrap, a draft
  // defaults to the workshop's last-picked model rather than always Auto.
  useEffect(() => {
    const key = currentSessionId ?? "draft";
    if (seededKey === key) return;
    if (models.length === 0) return;
    if (currentSessionId && !currentSession) return;
    const boot = !currentSessionId ? bootstrapRef.current : null;
    const draftFallback = !currentSessionId ? lastPickedModelId : null;
    const storedModelId = canonicalizeChatModelId(
      currentSession?.model ?? boot?.modelId ?? draftFallback ?? "auto",
    );
    const model =
      models.find((m) => m.id === storedModelId && !m.disabled) ??
      models.find((m) => m.id === "auto" && !m.disabled) ??
      models[0];
    if (!model) return;
    const preferredEffort =
      (currentSession?.reasoningEffort as ReasoningEffort | null) ??
      boot?.reasoningEffort ??
      (draftFallback && model.id === canonicalizeChatModelId(draftFallback) ? lastPickedEffort : null) ??
      null;
    seedSelection(
      model,
      resolveWorkshopEffort(model.reasoningOptions, preferredEffort),
      key,
    );
  }, [
    currentSessionId,
    currentSession,
    models,
    seedSelection,
    seededKey,
    lastPickedModelId,
    lastPickedEffort,
  ]);

  const seedComplete = seededKey === (currentSessionId ?? "draft");

  // Draft bootstrap: fire the first message once the picker is seeded and the
  // session list has settled. Always a new session — never the open thread.
  // Model/effort are passed explicitly so a rail race can't downgrade to Auto.
  useEffect(() => {
    if (seedKey === null) return;
    if (seedSentRef.current === seedKey) return;
    if (selectedSessionId === undefined) return;
    if (!seedComplete || sessionsLoading || isStreaming) return;
    const boot = bootstrapRef.current;
    seedSentRef.current = seedKey;
    onSeedConsumed?.();
    watchForThreadTitle();
    const seedPills = seedQuickCreates ?? [];
    const seedWords = seedMessage ?? "";
    void sendMessage(seedWords, {
      newSession: true,
      quickCreates: seedPills,
      onQuickCreateRefused: (gone) => {
        // Gone before the first send: the words and the pills wait in the box.
        setText((current) => current || seedWords);
        setPills(seedPills);
        setRefused(refusedIds(gone, seedPills));
      },
      ...(boot?.modelId ? { modelId: boot.modelId } : {}),
      ...(boot?.reasoningEffort ? { reasoningEffort: boot.reasoningEffort } : {}),
    });
  }, [
    seedKey,
    seedMessage,
    seedQuickCreates,
    selectedSessionId,
    seedComplete,
    sessionsLoading,
    isStreaming,
    sendMessage,
    onSeedConsumed,
    watchForThreadTitle,
  ]);

  const handleModelChange = (model: ChatModel) => {
    const effort = resolveWorkshopEffort(
      model.reasoningOptions,
      selectedReasoningEffort,
    );
    seedSelection(model, effort);
    setLastPicked(model.id, effort);
    // Only persist picks made after seeding — the composer's mount-time
    // default-pick must never overwrite the session's stored selection.
    if (seedComplete && currentSessionId) {
      updateSession.mutate({
        sessionId: currentSessionId,
        model: model.id,
        reasoningEffort: effort,
      });
    }
  };

  const handleEffortChange = (effort: ReasoningEffort) => {
    setSelectedReasoningEffort(effort);
    if (selectedModel) setLastPicked(selectedModel.id, effort);
    if (seedComplete && currentSessionId) {
      updateSession.mutate({ sessionId: currentSessionId, reasoningEffort: effort });
    }
  };

  // Files attached here belong to the PROJECT (every thread reads them); the
  // strip shows the ones attached in this thread.
  const fileEndpoints = useMemo(() => workshopAttachmentEndpoints(projectId), [projectId]);
  const attachments = useSessionAttachments(brandId, currentSessionId, fileEndpoints);
  const mediaQuery = useWorkshopMedia(projectId);
  const { upload: uploadImage } = useWorkshopMediaMutations(projectId);
  const [pendingImages, setPendingImages] = useState<
    { id: string; fileName: string; sessionId: string; createdAt: string }[]
  >([]);

  // Images attached in this thread, as the same transcript lines files get.
  const threadAttachments = useMemo<ChatAttachmentItem[]>(() => {
    if (!currentSessionId) return [];
    const uploaded: ChatAttachmentItem[] = (mediaQuery.data ?? [])
      .filter(
        (m) =>
          !m.pending &&
          m.attribution?.createdBy.actor === "user" &&
          m.attribution.createdBy.thread?.id === currentSessionId,
      )
      .map((m) => ({
        id: m.id,
        fileName: m.fileName,
        kind: "image" as const,
        state: "completed" as const,
        createdAt: m.linkedAt,
        authorUserId: m.attribution?.createdBy.userId ?? null,
        authorName: m.attribution?.createdBy.userName ?? null,
        // Media is rounded in Workshop; the picture fills its thumbnail.
        previewUrl: m.thumbUrl ?? m.url ?? null,
      }));
    const inFlight: ChatAttachmentItem[] = pendingImages
      .filter((p) => p.sessionId === currentSessionId)
      .map((p) => ({
        id: p.id,
        fileName: p.fileName,
        kind: "image",
        state: "uploading",
        createdAt: p.createdAt,
        authorUserId: viewerPerson?.id ?? null,
        authorName: viewerPerson?.name ?? null,
      }));
    const files = attachments.items.map((item) =>
      item.state === "uploading" && !item.authorUserId && viewerPerson
        ? { ...item, authorUserId: viewerPerson.id, authorName: viewerPerson.name }
        : item,
    );
    return [...files, ...uploaded, ...inFlight].sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
  }, [attachments.items, mediaQuery.data, pendingImages, currentSessionId, viewerPerson]);

  // Read a page at a time: files from before the oldest message loaded wait
  // with their page (they would otherwise all land on that message).
  const windowStart = paging.hasOlder ? (oldestStored(displayMessages)?.createdAt ?? null) : null;
  const windowedAttachments = useMemo(
    () =>
      windowStart
        ? threadAttachments.filter((item) => item.local || item.createdAt >= windowStart)
        : threadAttachments,
    [threadAttachments, windowStart],
  );

  /* The files waiting in the composer go out with the message being sent. A
     private thread claims them by that message's position among yours, because
     its time comes from this device and the files' from the server. A shared
     thread places files by author, so it needs no claim. Positions count the
     pages not loaded too (userRowOffset), so reading the thread back keeps them. */
  const [claims, setClaims] = useState<AttachmentClaims>({});
  const claimStaged = () => {
    if (isShared) return;
    const position = chatPaging.userRowOffset + messages.filter((m) => m.role === "user").length;
    const { staged } = placeThreadFiles(
      messages,
      windowedAttachments,
      claims,
      null,
      false,
      chatPaging.userRowOffset,
    );
    const ids = staged.filter((item) => !item.local).map((item) => item.id);
    if (ids.length === 0) return;
    setClaims((prev) => ({ ...prev, ...Object.fromEntries(ids.map((id) => [id, position])) }));
  };

  const { isQueued, queueOrSend } = useDeferredSend({
    attachments: threadAttachments,
    sessionId: currentSessionId,
    onSend: (t) => {
      watchForThreadTitle();
      claimStaged();
      const sentPills = sendingPillsRef.current;
      sendingPillsRef.current = [];
      const onRefused = (gone: string) => {
        // A pick went while it sat in the draft: the words and the pills come back.
        setText((current) => current || t);
        setPills(sentPills);
        setRefused(refusedIds(gone, sentPills));
      };
      if (isShared) {
        // A refused send gives the text back, unless something new was typed.
        void shared.sendMessage(t, { quickCreates: sentPills, onQuickCreateRefused: onRefused }).then((ok) => {
          if (!ok) setText((current) => current || t);
        });
        return;
      }
      void sendMessage(t, { quickCreates: sentPills, onQuickCreateRefused: onRefused });
    },
  });

  /** A file can be attached before the first message: that opens the thread. */
  const ensureThread = async (): Promise<string | null> => {
    if (currentSessionId) return currentSessionId;
    try {
      const session = await createSession.mutateAsync();
      handleSessionCreated(session);
      return session.id;
    } catch (error) {
      queue.add({
        title: "Couldn't start a chat for the file",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
      return null;
    }
  };

  const handleAttachFiles = async (files: File[]) => {
    const sessionId = await ensureThread();
    if (!sessionId) return;
    const images = files.filter(isImageFile);
    const others = files.filter((f) => !isImageFile(f));
    if (others.length > 0) {
      // Rejects anything that is not a PDF, video, spreadsheet or text file,
      // visibly, in the strip.
      void attachments.upload(others, sessionId, (documentId) => {
        onUploadAdded?.("context", documentId);
        invalidateSessions();
        refreshThreadTitleSoon();
      });
    }
    for (const file of images) {
      const previewId = newMediaPreviewId();
      setPendingImages((current) => [
        ...current,
        { id: previewId, fileName: file.name, sessionId, createdAt: new Date().toISOString() },
      ]);
      uploadImage.mutate(
        { file, sessionId, previewId },
        {
          onSuccess: (media) => {
            onUploadAdded?.("media", media.id);
            invalidateSessions();
            refreshThreadTitleSoon();
          },
          onSettled: () =>
            setPendingImages((current) => current.filter((p) => p.id !== previewId)),
        },
      );
    }
  };

  const canAttach = canWrite && !archived;

  const seededFilesRef = useRef<File[] | null>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const composerFromConsumedRef = useRef(onComposerFromConsumed);
  // The latest attach, for the seeded-files effect: it runs when the seed
  // arrives, not on every render the attach function is rebuilt.
  const handleAttachFilesRef = useRef(handleAttachFiles);
  // Declared before the composer's slide below, so it reads the current callbacks.
  useLayoutEffect(() => {
    composerFromConsumedRef.current = onComposerFromConsumed;
    handleAttachFilesRef.current = handleAttachFiles;
  });
  // The dock floats over the transcript. Its height is the gap the log keeps
  // clear, so the last message can rest above the composer.
  useLayoutEffect(() => {
    const dock = dockRef.current;
    const frame = frameRef.current;
    if (!dock || !frame) return;
    const apply = () => frame.style.setProperty("--ws-dock-h", `${dock.offsetHeight}px`);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(dock);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!seedFiles?.length || !seedThreadId || currentSessionId !== seedThreadId) return;
    if (seededFilesRef.current === seedFiles) return;
    seededFilesRef.current = seedFiles;
    onSeedFilesConsumed?.();
    void handleAttachFilesRef.current(seedFiles);
  }, [seedFiles, seedThreadId, currentSessionId, onSeedFilesConsumed]);

  useLayoutEffect(() => {
    const from = composerFrom;
    const frame = frameRef.current;
    if (!from || !frame) return;
    const form = frame.querySelector<HTMLElement>("form");
    if (!form) {
      composerFromConsumedRef.current?.();
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      composerFromConsumedRef.current?.();
      return;
    }
    const next = form.getBoundingClientRect();
    const dx = from.left + from.width / 2 - (next.left + next.width / 2);
    const dy = from.top - next.top;
    form.style.transition = "none";
    form.style.transform = `translate(${dx}px, ${dy}px)`;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      form.style.transition = "";
      form.style.transform = "";
      composerFromConsumedRef.current?.();
    };
    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        form.style.transition = "transform 560ms cubic-bezier(0.16, 1, 0.3, 1)";
        form.style.transform = "translate(0, 0)";
      });
    });
    const onEnd = (event: TransitionEvent) => {
      if (event.propertyName !== "transform") return;
      finish();
    };
    form.addEventListener("transitionend", onEnd);
    const timeout = window.setTimeout(finish, 700);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeout);
      form.removeEventListener("transitionend", onEnd);
      // StrictMode replays this effect on mount. Without a reset the replay
      // would measure the already-offset rect, compute a zero delta, and the
      // composer would snap straight to the bottom instead of gliding.
      if (!settled) {
        form.style.transition = "none";
        form.style.transform = "";
      }
    };
  }, [composerFrom]);

  const isInitialChatLoading = sessionsLoading || displayLoadingMessages;
  // Only a private reply locks the thread (it streams into whichever is open).
  const threadSwitchBusy = isStreaming || isInitialChatLoading;
  // In a shared thread people keep writing while the assistant works: what
  // they send joins its current reply.
  const isChatBusy = (isShared ? isInitialChatLoading : threadSwitchBusy) || isQueued;

  // The agent put a card up and is waiting on it — hold the composer shut. Not
  // in a shared thread: anyone there may answer it, or just keep talking.
  const awaitingResponse = !isShared && findPendingInteraction(displayMessages) !== null;

  // The thread follows the conversation (a reply, someone's message, your own
  // send) only while the viewer is at its newest end: scrolled up to read,
  // nothing pulls them away. Their own send always brings them back down.
  const followRef = useRef(true);
  const pagingRef = useRef(paging);
  const displayCountRef = useRef(displayMessages.length);
  useLayoutEffect(() => {
    pagingRef.current = paging;
    displayCountRef.current = displayMessages.length;
  });
  /** A page being read in above: the message the viewer was on stays where it is. */
  const anchorRef = useRef<{ el: Element; top: number } | null>(null);
  const trimTimerRef = useRef<number | null>(null);
  /** When reading a page above last failed: no retry before OLDER_RETRY_MS. */
  const olderFailedAtRef = useRef(0);

  /** Reads the page above the oldest message, holding on to what the viewer is looking at. */
  const readOlder = useCallback(() => {
    const el = scrollRef.current;
    const p = pagingRef.current;
    if (!el || !p.hasOlder || p.isLoadingOlder || anchorRef.current) return;
    if (Date.now() - olderFailedAtRef.current < OLDER_RETRY_MS) return;
    const box = el.getBoundingClientRect();
    const first = Array.from(el.querySelectorAll(".m7r-you, .m7r-turn")).find(
      (node) => node.getBoundingClientRect().bottom > box.top,
    );
    anchorRef.current = first ? { el: first, top: first.getBoundingClientRect().top } : null;
    void p.loadOlder().then((ok) => {
      if (!ok) olderFailedAtRef.current = Date.now();
    });
  }, []);

  /**
   * At the newest end, once the scroll has settled: the pages read on the way
   * up go again, so a long thread only ever holds about a page.
   */
  const scheduleTrim = useCallback(() => {
    if (trimTimerRef.current !== null) window.clearTimeout(trimTimerRef.current);
    trimTimerRef.current = window.setTimeout(() => {
      trimTimerRef.current = null;
      if (followRef.current && !anchorRef.current && displayCountRef.current > THREAD_TRIM_AT) {
        pagingRef.current.trimOlder();
      }
    }, TRIM_SETTLE_MS);
  }, []);

  useEffect(() => {
    followRef.current = true;
    anchorRef.current = null;
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      followRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_SLACK_PX;
      // Still scrolling while the page above loads: keep up with where the
      // message being held on to is now.
      const anchor = anchorRef.current;
      if (anchor) anchor.top = anchor.el.getBoundingClientRect().top;
      if (el.scrollTop < LOAD_OLDER_PX) readOlder();
      if (followRef.current) scheduleTrim();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (trimTimerRef.current !== null) window.clearTimeout(trimTimerRef.current);
      trimTimerRef.current = null;
    };
  }, [currentSessionId, readOlder, scheduleTrim]);

  // Before paint, so nothing flashes: the page that came in above keeps the
  // viewer on the message they were reading; otherwise a viewer at the newest
  // end stays there as the thread grows.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const anchor = anchorRef.current;
    if (anchor) {
      if (paging.isLoadingOlder) return;
      anchorRef.current = null;
      if (anchor.el.isConnected) {
        el.scrollTop += anchor.el.getBoundingClientRect().top - anchor.top;
      }
      return;
    }
    if (!followRef.current) return;
    el.scrollTop = el.scrollHeight;
    if (displayMessages.length > THREAD_TRIM_AT) scheduleTrim();
  }, [displayMessages, displayBlocks, paging.isLoadingOlder, scheduleTrim]);

  // A first page too short to scroll: read further back until it fills the
  // thread (or the thread has no more), or there would be nothing to scroll up.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || isInitialChatLoading || !paging.hasOlder || paging.isLoadingOlder) return;
    if (el.scrollHeight - el.clientHeight < LOAD_OLDER_PX) readOlder();
  }, [displayMessages, paging.hasOlder, paging.isLoadingOlder, isInitialChatLoading, readOlder]);

  // A private reply is being written (or a send waits on its file): what the
  // person sends meanwhile queues behind it. A shared thread takes it at once.
  const replyLive = !isShared && (isStreaming || isQueued);
  const canQueue = replyLive && !isInitialChatLoading;
  const queuedHere = isShared ? [] : listQueued(currentSessionId);

  const handleSend = (from: { left: number; top: number } | null = null) => {
    const trimmed = text.trim();
    if ((!trimmed && pills.length === 0) || unavailablePills.length > 0 || awaitingResponse) return;
    // The bubble lands with exactly these words: it floats up from the field.
    // Queued or joined into another send, it just appears where it lands.
    const flyFrom = (words: string) => {
      if (from) flightRef.current = { ...from, text: words, at: performance.now() };
    };
    if (pills.length > 0) {
      // A Quick Create goes on its own, never behind a reply in the queue.
      if (isChatBusy) return;
      followRef.current = true;
      flyFrom(trimmed);
      setText("");
      setPills([]);
      setRefused([]);
      sendingPillsRef.current = pills;
      queueOrSend(trimmed);
      return;
    }
    // A send that has not left yet takes this one in (its gather window), so
    // two quick sends are read as one turn; only after it left does the next
    // wait in the queue for the reply.
    if (!isShared && joinSend(trimmed)) {
      followRef.current = true;
      setText("");
      return;
    }
    if (canQueue) {
      enqueue(currentSessionId, trimmed);
      setText("");
      return;
    }
    if (isChatBusy) return;
    followRef.current = true;
    setText("");
    // Anything still queued goes first, in the same message. Holds the turn
    // until a just-attached file is readable, then sends it.
    const waiting = takeQueued(currentSessionId).map((m) => m.text);
    if (waiting.length === 0) flyFrom(trimmed);
    queueOrSend(joinQueued([...waiting, trimmed]));
  };

  // A message that never reached Mohtawa waits above its own thread's
  // composer (one per thread; a thread not created yet keeps its own), and only
  // Try again, Edit or Discard ends it.
  const unsentHere = isShared ? null : (unsentByThread[unsentKey(currentSessionId)] ?? null);
  const retryUnsent = async () => {
    if (isChatBusy) return;
    const entry = takeUnsent(currentSessionId);
    if (!entry) return;
    // The thread is read first when it has to be: parts that reached it after
    // all are dropped and their reply followed, so nothing is sent twice.
    const found = await unsentRemainder(entry);
    if (!found) {
      // Still no answer: it waits as it was, unchecked, and nothing goes blind.
      holdUnsent({ ...entry, checked: false });
      return;
    }
    if (found.rest.length === 0) return;
    if (found.rest.length < unsentParts(entry).length) {
      // Part of it is in the thread and being answered: the rest waits, checked,
      // for the person to send once that reply is done.
      holdUnsent({
        ...entry,
        parts: found.rest,
        text: found.rest.join("\n\n"),
        checked: true,
        userRowsBefore: found.userRows,
      });
      return;
    }
    followRef.current = true;
    queueOrSend(found.rest.join("\n\n"));
  };
  const editUnsent = () => {
    const entry = takeUnsent(currentSessionId);
    if (!entry) return;
    // Back in the box, after anything typed since, so nothing is lost.
    setText((current) =>
      current.trim() ? `${current.trimEnd()}\n\n${entry.text}` : entry.text,
    );
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };
  // Gone at once, with five seconds to take it back (stage 1's rule).
  const discardUnsent = () => {
    const entry = takeUnsent(currentSessionId);
    if (!entry) return;
    undoToast.show({
      icon: Trash2,
      title: "Message discarded",
      line: "It was never sent. Undo puts it back above the composer.",
      undo: () => holdUnsent(entry),
    });
  };

  // Stopping a reply holds the queue: the person stopped it to steer.
  const stopPressedRef = useRef(false);
  const handleStop = () => {
    stopPressedRef.current = true;
    stopStreaming();
  };

  /** Everything queued in this thread, now, as one message. */
  const sendQueued = () => {
    if (isChatBusy || awaitingResponse) return;
    const waiting = takeQueued(currentSessionId);
    if (waiting.length === 0) return;
    followRef.current = true;
    queueOrSend(joinQueued(waiting.map((m) => m.text)));
  };

  // The reply the queue waited on has ended. Ended cleanly, the queue goes at
  // once, as one message. Stopped, failed, not sent, or ended on a card that
  // needs an answer, it stays above the composer for the person to send.
  const replyWasLiveRef = useRef(false);
  const lastStatus = messages[messages.length - 1]?.status;
  useEffect(() => {
    const was = replyWasLiveRef.current;
    replyWasLiveRef.current = replyLive;
    if (!was || replyLive) return;
    const stopped = stopPressedRef.current;
    stopPressedRef.current = false;
    if (stopped || lastStatus === "aborted" || streamError || unsentHere) return;
    if (awaitingResponse || isInitialChatLoading) return;
    const waiting = takeQueued(currentSessionId);
    if (waiting.length === 0) return;
    followRef.current = true;
    queueOrSend(joinQueued(waiting.map((m) => m.text)));
  }, [
    replyLive,
    lastStatus,
    streamError,
    unsentHere,
    awaitingResponse,
    isInitialChatLoading,
    takeQueued,
    currentSessionId,
    queueOrSend,
  ]);

  /** Back in the box, after anything typed since, so nothing is lost. */
  const editQueued = (id: string) => {
    const found = unqueue(currentSessionId, id);
    if (!found) return;
    setText((current) =>
      current.trim() ? `${current.trimEnd()}\n\n${found.item.text}` : found.item.text,
    );
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };
  // Gone at once, with five seconds to take it back, in its place in the line.
  const removeQueued = (id: string) => {
    const threadId = currentSessionId;
    const found = unqueue(threadId, id);
    if (!found) return;
    undoToast.show({
      icon: Trash2,
      title: "Queued message removed",
      line: "It was never sent. Undo puts it back in the queue.",
      undo: () => requeue(threadId, found.item, found.index),
    });
  };

  // What the open card asks for, so the composer can say what to do first.
  const pendingCard = awaitingResponse
    ? displayMessages[displayMessages.length - 1]?.toolEvents?.some(
        (event) => parseAskUserQuestions(event) !== null,
      )
      ? "questions"
      : "values"
    : null;


  const [renameOpen, setRenameOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");

  // Threads only split the conversation; the side panel is project-wide and
  // stays as it is. Switching is locked while a reply streams (the stream
  // writes into whichever thread is open), so none of these can race a turn.
  const openThread = (sessionId: string | null) => {
    if (sessionId === currentSessionId) return;
    clearMessages();
    setSelectedSessionId(sessionId);
  };

  // A thread that leaves the list while open was deleted by its OP, or the
  // viewer was taken out of it: move off it rather than keep a dead thread that
  // looks like their own. A thread not listed yet (just created) is left alone.
  const listedThreadRef = useRef<string | null>(null);
  useEffect(() => {
    if (sessionsLoading || !currentSessionId) return;
    if (currentSession) {
      listedThreadRef.current = currentSession.id;
      return;
    }
    if (listedThreadRef.current !== currentSessionId) return;
    listedThreadRef.current = null;
    queue.add({
      title: "This chat is no longer available",
      description: "It was deleted, or you were removed from it.",
      variant: "info",
    });
    clearMessages();
     
    setSelectedSessionId(sessions[0]?.id ?? null);
  }, [sessionsLoading, currentSessionId, currentSession, sessions, clearMessages]);

  /** Move off a thread that is going: the page's landing, else the next one. */
  const moveOff = (sessionId: string) => {
    if (sessionId !== currentSessionId) return;
    if (onThreadGone) {
      onThreadGone(sessionId);
      return;
    }
    // The cache drops the row a tick later, so pick the successor now rather
    // than letting the pin above land back on the thread being deleted.
    openThread(sessions.find((s) => s.id !== sessionId)?.id ?? null);
  };

  /** Only the person who started a thread deletes it (permanent: asked first). */
  const handleDeleteThread = (sessionId: string) => {
    setDeleteConfirmOpen(false);
    moveOff(sessionId);
    deleteSession.mutate(sessionId);
  };

  /**
   * Anyone else leaves it. The API cannot take a leave back (only the owner can
   * add you again), so it is held for five seconds with an Undo before it is
   * sent.
   */
  const handleLeaveThread = (sessionId: string) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (!session || !viewer) return;
    moveOff(sessionId);
    undoToast.hold({
      id: heldThreadKey(sessionId),
      icon: LogOut,
      title: `You left “${session.title.trim() || "New chat"}”`,
      line: "Its owner can add you back.",
      commit: () => removeParticipant.mutate({ sessionId, userId: viewer.id, leaving: true }),
      commitOnUnload: () => leaveOnUnload(sessionId, viewer.id),
    });
  };

  useImperativeHandle(
    ref,
    () => ({
      prefillComposer: (message: string) => {
        // Appended, never replacing: anything the user had half-typed stays.
        setText((current) =>
          current.trim() ? `${current.trimEnd()}\n\n${message}` : message,
        );
        window.setTimeout(() => {
          const el = textareaRef.current;
          if (!el) return;
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }, PREFILL_FOCUS_DELAY_MS);
      },
      quoteInComposer: (passage: string) => {
        const quote = quotePassage(passage);
        if (!quote) return;
        // At the top, never replacing: anything the user had half-typed stays under it.
        setText((current) =>
          current.trim() ? `${quote}\n\n${current.trimStart()}` : `${quote}\n\n`,
        );
        window.setTimeout(() => {
          const el = textareaRef.current;
          if (!el) return;
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }, PREFILL_FOCUS_DELAY_MS);
      },
    }),
    [],
  );

  const contextWindow = selectedModel?.contextWindow ?? 0;
  const contextRatio =
    contextTokens !== null && contextWindow > 0 ? contextTokens / contextWindow : 0;
  // The last reply's size is only measured again on the next reply, so right
  // after compacting the ratio still reads high: nothing new since the summary
  // means there is nothing to compact.
  const lastMessageId = displayMessages[displayMessages.length - 1]?.id ?? null;
  const justCompacted =
    !!lastMessageId && currentSession?.compactedAtMessageId === lastMessageId;
  const showCompactNudge =
    (compactSession.isPending ||
      (contextRatio > COMPACT_NUDGE_RATIO && !justCompacted)) &&
    !!currentSessionId &&
    !isStreaming &&
    isThreadOwner &&
    !isShared;

  const handleCompact = () => {
    if (!currentSessionId || compactSession.isPending) return;
    compactSession.mutate(currentSessionId, {
      onSuccess: () => {
        queue.add({
          title: "Conversation compacted",
          description:
            "Older messages are summarized for the assistant; your transcript is unchanged.",
          variant: "success",
        });
      },
      onError: (error) => {
        queue.add({
          title: "Couldn't compact",
          description: getAiRequestErrorMessage(error),
          variant: "warning",
        });
      },
    });
  };

  // A fresh thread in a project that already has work: say what carries over.
  const isNewThread = currentSessionId === null && sessions.length > 0;
  const emptyState = useMemo(
    () =>
      isNewThread ? (
        <div className="ws-project-empty">
          <MessagesSquare size={24} strokeWidth={1.5} aria-hidden />
          <h2>New chat in {projectName}</h2>
          <p>
            This conversation starts clean. The project&apos;s artifacts, ideas,
            context, media and agents are shared by every thread, so pick up any of
            them here.
          </p>
        </div>
      ) : (
        <div className="ws-project-empty">
          <Briefcase size={24} strokeWidth={1.5} aria-hidden />
          <h2>Build {projectName} together</h2>
          <p>
            Research the problem, develop the thinking, create versioned deliverables,
            or send a specialist agent into a longer task.
          </p>
        </div>
      ),
    [isNewThread, projectName],
  );

  // Blank-thread → first-send transition. The hero unmounts the instant a send
  // starts streaming, so a snapshot of it replays as an overlay that sinks
  // away while the first transcript rows rise beneath. The latch arms only
  // while the hero is actually on screen, so the state churn around a send
  // (session created, URL sync, title refresh, stream chunks) can neither
  // cancel nor replay the animation.
  const [departingHero, setDepartingHero] = useState<ReactNode>(null);
  const [threadEntering, setThreadEntering] = useState(false);
  const heroWasShowingRef = useRef(false);
  const heroExitTimerRef = useRef<number | null>(null);

  // Matches what WorkshopConversation shows: the skeleton while fetching, the
  // hero once idle and empty.
  const heroShowing =
    !isInitialChatLoading &&
    !displayStreaming &&
    displayMessages.length === 0 &&
    displayBlocks.length === 0 &&
    threadAttachments.length === 0;

  useEffect(() => {
    if (heroShowing) {
      heroWasShowingRef.current = true;
      // An error can bring the hero back mid-exit — drop the overlay at once.
       
      setDepartingHero(null);
      setThreadEntering(false);
      return;
    }
    if (!heroWasShowingRef.current) return;
    heroWasShowingRef.current = false;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    setDepartingHero(emptyState);
    setThreadEntering(true);
    if (heroExitTimerRef.current !== null) {
      window.clearTimeout(heroExitTimerRef.current);
    }
    heroExitTimerRef.current = window.setTimeout(
      () => setDepartingHero(null),
      HERO_EXIT_MS,
    );
    // The latch above makes this a no-op on every run but the one true→false
    // flip that starts the exit.
  }, [heroShowing, emptyState]);

  // Close the enter window once the first real row has finished rising; if the
  // turn errors before one lands, close it immediately.
  useEffect(() => {
    if (!threadEntering) return;
    const firstRowLanded = displayMessages.length > 0 || threadAttachments.length > 0;
    if (!firstRowLanded && displayStreaming) return;
    const timer = window.setTimeout(
      () => setThreadEntering(false),
      firstRowLanded ? THREAD_ENTER_MS : 0,
    );
    return () => window.clearTimeout(timer);
  }, [threadEntering, displayMessages.length, threadAttachments.length, displayStreaming]);

  useEffect(
    () => () => {
      if (heroExitTimerRef.current !== null) {
        window.clearTimeout(heroExitTimerRef.current);
      }
    },
    [],
  );

  const threadTitle = currentSession?.title.trim() || "New chat";

  // Shared threads: everyone in it but the viewer, and who has it open now.
  const threadPeople = useMemo<WorkshopPerson[] | null>(() => {
    if (!isShared || !currentSession) return null;
    const all = [
      ...(currentSession.owner ? [currentSession.owner] : []),
      ...(currentSession.participants ?? []),
    ];
    return all.filter((p, i) => p.userId !== viewer?.id && all.findIndex((q) => q.userId === p.userId) === i);
  }, [isShared, currentSession, viewer?.id]);
  const hereNow = useMemo(
    () => shared.present.filter((p) => p.userId !== viewer?.id),
    [shared.present, viewer?.id],
  );

  const threadMeta = useMemo(() => {
    if (!currentSession) return "";
    const at = new Date(currentSession.lastMessageAt);
    const when = Number.isNaN(at.getTime())
      ? ""
      : `${at.toLocaleDateString(undefined, { month: "short", day: "numeric" })} · ${at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
    const who = threadPeople?.length ? `Shared with ${threadPeople.length}` : "Only you";
    return [when, who].filter(Boolean).join(" · ").toUpperCase();
  }, [currentSession, threadPeople]);

  const handleMenu = (action: ChatMenuAction) => {
    if (!currentSessionId) return;
    if (action === "rename") {
      // On the desk the chat's row in the sidebar becomes the field.
      if (desk && !isPhone) {
        desk.startRename(currentSessionId);
        return;
      }
      setRenameDraft(threadTitle);
      setRenameOpen(true);
    } else if (action === "share") {
      setShareOpen(true);
    } else if (action === "delete") {
      setDeleteConfirmOpen(true);
    } else {
      setLeaveOpen(true);
    }
  };

  const drop = useThreadDrop((files) => void handleAttachFiles(files), !canAttach || isStreaming);
  // The file list arrives before the transcript. Until the transcript is here,
  // every file already in the thread looks unsent and would sit in the composer.
  const transcriptReady = isShared ? !shared.isLoadingMessages : messagesLoaded;
  const staged = placeThreadFiles(
    displayMessages,
    transcriptReady ? windowedAttachments : [],
    claims,
    isShared ? (viewer?.id ?? null) : null,
    isShared,
    paging.userRowOffset,
  ).staged;

  const placeholder = pendingCard
    ? pendingCard === "questions"
      ? "Answer the question above to continue"
      : "Check the values above to continue"
    : isShared
      ? "Message everyone in this chat"
      : canQueue && isStreaming
        ? isPhone
          ? "Queue a message"
          : "Queue a message for after this reply"
        : "Ask Workshop";
  const composerPlaceholder = placeholder;

  return (
    <div
      ref={frameRef}
      className={`ws-thread flex h-full min-h-0 flex-col overflow-hidden${
        threadEntering ? " ws-thread--enter" : ""
      }`}
      data-phone={isPhone || undefined}
      {...drop.handlers}
    >
      <WorkshopThreadHead
        title={threadTitle}
        people={threadPeople}
        hereNow={hereNow}
        onOpenPeople={
          isThreadOwner && canWrite && !archived && currentSession
            ? () => setShareOpen(true)
            : undefined
        }
        archived={archived}
        panelOpen={libraryOpen}
        onTogglePanel={onOpenLibrary ? () => onOpenLibrary(openRoomSection ?? "outputs") : undefined}
        menu={
          currentSessionId
            ? {
                meta: threadMeta,
                isOwner: isThreadOwner,
                isShared,
                canEdit: canWrite && !archived,
                // A private reply streams to this tab alone; share once it ends.
                shareDisabled: isStreaming,
                deleteDisabled: threadSwitchBusy || deleteSession.isPending,
                projectId,
                projects: moveProjects,
                onAction: handleMenu,
                onMove: moveChat,
                onMoveToNewProject: () => desk?.openNewProject(moveChat),
              }
            : null
        }
        isPhone={isPhone}
        lane={lane}
        onOpenSidebar={onOpenProjectSheet}
      />

      <div className="ws-thread-body">
        <WorkshopConversation
          scrollRef={scrollRef}
          flightRef={flightRef}
          messages={displayMessages}
          attachments={windowedAttachments}
          olderPages={{ hasOlder: paging.hasOlder, loading: paging.isLoadingOlder }}
          claimOffset={paging.userRowOffset}
          linkedAttachments={attachments.linked}
          attachmentClaims={claims}
          isLoading={isInitialChatLoading}
          isStreaming={displayStreaming}
          streamingBlocks={displayBlocks}
          streamingAt={isShared ? shared.streamingAt : null}
          streamError={isShared ? null : streamError}
          models={models}
          emptyState={emptyState}
          shared={
            isShared
              ? {
                  viewerUserId: viewer?.id ?? null,
                  sharedAt: currentSession?.sharedAt ?? null,
                  lastSeenMessageId: shared.lastSeenMessageId,
                }
              : null
          }
          compactedAtMessageId={currentSession?.compactedAtMessageId ?? null}
          onRespond={(reply, cardMessageId) => {
            // A shared thread names the card, so only the first answer to it
            // reaches the agent. A refused one needs no retry: the card shows
            // who answered once theirs lands.
            if (isShared) void shared.sendMessage(reply, { respondsTo: cardMessageId });
            else queueOrSend(reply);
          }}
          respondDisabled={isChatBusy}
          onRetryAttachment={(id) => void attachments.retry(id)}
          renderOutputs={renderOutputs}
          ariaLabel={`${projectName} Workshop conversation`}
        />
        {departingHero ? (
          <div className="ws-chat-hero-exit" aria-hidden="true">
            {departingHero}
          </div>
        ) : null}
      </div>

      <div ref={dockRef} className="ws-dock">
        {isShared && shared.loadFailed ? (
          <div className="ws-note" role="alert">
            <p className="ws-note-line ws-note-body">Couldn't load this chat. It may be out of date.</p>
            <button type="button" className="ws-link" onClick={() => void shared.reload()}>
              Try again
            </button>
          </div>
        ) : null}
        {isShared ? (
          <ThreadTypingLine typing={shared.typing} />
        ) : null}
        {unsentHere ? (
          <UnsentMessage
            text={unsentHere.text}
            checked={unsentHere.checked}
            onRetry={() => void retryUnsent()}
            onEdit={editUnsent}
            onDiscard={discardUnsent}
            retryDisabled={isChatBusy}
          />
        ) : null}
        {showCompactNudge ? (
          <CompactNudge
            percent={Math.round(contextRatio * 100)}
            compacting={compactSession.isPending}
            onCompact={handleCompact}
          />
        ) : null}
        {queuedHere.length > 0 ? (
          <QueuedMessages
            items={queuedHere}
            state={replyLive ? "reply" : awaitingResponse ? "card" : "held"}
            onSendNow={sendQueued}
            sendDisabled={isChatBusy}
            onEdit={editQueued}
            onRemove={removeQueued}
          />
        ) : null}
        <QuickCreateGone items={unavailablePills} />
        <WorkshopComposer
          voice={voice}
          value={text}
          onChange={(value) => {
            setText(value);
            if (isShared) shared.notifyTyping(value);
          }}
          onSubmit={handleSend}
          placeholder={composerPlaceholder}
          inputLabel="Message the Workshop"
          disabled={(isChatBusy && !canQueue) || awaitingResponse}
          isStreaming={displayStreaming}
          onStop={isShared ? shared.stop : handleStop}
          isStopping={isShared ? shared.isStopping : isStopping}
          sendWhileStreaming={isShared || (canQueue && pills.length === 0)}
          queueing={canQueue}
          showModel={!isShared}
          models={models}
          selectedModel={selectedModel}
          onModelChange={handleModelChange}
          effort={selectedReasoningEffort}
          onEffortChange={handleEffortChange}
          textareaRef={textareaRef}
          onAttachFiles={canAttach ? (files) => void handleAttachFiles(files) : undefined}
          attachDisabled={isStreaming}
          attachAccept={WORKSHOP_ATTACH_ACCEPT}
          photoAccept={WORKSHOP_PHOTO_ACCEPT}
          fileAccept={WORKSHOP_DOC_ACCEPT}
          staged={staged}
          onRetryAttachment={(id) => void attachments.retry(id)}
          queued={isQueued}
          isPhone={isPhone}
          quickCreates={pills}
          unavailableQuickCreates={unavailablePills}
          onRemoveQuickCreate={(item) => {
            setPills((prev) => removePick(prev, item));
            setRefused((prev) => prev.filter((id) => id !== pickId(item)));
          }}
          quickCreateItems={canWrite && !archived ? quickItems : undefined}
          onPickQuickCreate={pickQuickCreate}
        />
      </div>

      {drop.active ? <ThreadDropTarget /> : null}

      <LeaveChatDialog
        session={leaveOpen ? (currentSession ?? null) : null}
        onCancel={() => setLeaveOpen(false)}
        onLeave={(session) => {
          setLeaveOpen(false);
          handleLeaveThread(session.id);
        }}
      />

      <WorkshopDialog
        isOpen={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete this chat?"
        description={`“${threadTitle}” and its messages go for everyone in it. The outputs it made stay in the project.`}
        consequence="This cannot be undone"
        cancelLabel="Keep chat"
        confirmLabel="Delete chat"
        confirmTone="danger"
        confirmDisabled={!currentSessionId}
        onConfirm={() => {
          if (!currentSessionId) return;
          handleDeleteThread(currentSessionId);
        }}
      />

      <WorkshopDialog
        isOpen={renameOpen}
        onOpenChange={setRenameOpen}
        title="Rename this chat"
        description={
          isShared ? "Everyone in the chat sees the new name." : "Only you see this chat."
        }
        cancelLabel="Cancel"
        confirmLabel="Rename"
        confirmDisabled={
          !currentSessionId || !renameDraft.trim() || renameDraft.trim() === threadTitle
        }
        onSubmit={() => {
          const title = renameDraft.trim();
          if (!currentSessionId || !title) return;
          setRenameOpen(false);
          renameSession.mutate({ sessionId: currentSessionId, title });
        }}
      >
        <KitInput
          label="Name"
          value={renameDraft}
          onChange={(e) => setRenameDraft(e.target.value)}
          maxLength={THREAD_TITLE_MAX}
          autoFocus
        />
      </WorkshopDialog>

      {currentSession && shareOpen ? (
        <ShareThreadModal
          projectId={projectId}
          session={currentSession}
          isOpen={shareOpen}
          onOpenChange={setShareOpen}
          onManageProject={onManageProject}
        />
      ) : null}
    </div>
  );
}
